import { useEffect, useState } from "react";
import { useParams, useSearchParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { Button } from "../components/ui/button";
import { toast } from "sonner";
import { Loader2, Church, Route as RouteIcon, TentTree, Home, User as UserIcon, CheckCircle2, XCircle, DollarSign, Lock, ScanLine, ArrowLeft } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_ORDER = ["not_checked_in", "at_church", "left_church", "at_camp", "left_camp", "back_at_church"];
const STATUS_META = {
  not_checked_in:  { label: "Sin registrar",          icon: UserIcon,  color: "bg-gray-600" },
  at_church:       { label: "En iglesia",             icon: Church,    color: "bg-blue-500" },
  left_church:     { label: "Salí de la iglesia",     icon: RouteIcon, color: "bg-cyan-500" },
  at_camp:         { label: "Llegué al campamento",   icon: TentTree,  color: "bg-green-500" },
  left_camp:       { label: "Salí del campamento",    icon: RouteIcon, color: "bg-orange-500" },
  back_at_church:  { label: "De regreso en iglesia",  icon: Home,      color: "bg-purple-500" },
};
const STATUS_INDEX = STATUS_ORDER.reduce((a, s, i) => { a[s] = i; return a; }, {});

export default function CheckIn() {
  const { id } = useParams();
  const [sp] = useSearchParams();
  const token = sp.get("t") || sp.get("token") || "";
  const nav = useNavigate();
  const [info, setInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(null);
  const [error, setError] = useState(null);

  const load = async () => {
    try {
      const { data } = await axios.get(`${API}/public/checkin/${id}?token=${encodeURIComponent(token)}`, { timeout: 15000 });
      setInfo(data);
      setError(null);
    } catch (e) {
      setError(e?.response?.data?.detail || (e?.code === "ECONNABORTED" ? "Tiempo de espera agotado — reintenta" : "No se pudo cargar"));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!token) { setError("Falta token"); setLoading(false); return; }
    setLoading(true);
    load();
  }, [id, token]);

  // Detect if we came from the QR scanner (has any /kiosko referrer OR came via scanner)
  const isKioskoUser = !!localStorage.getItem("admin_token");

  const update = async (status) => {
    setSaving(status);
    try {
      await axios.post(`${API}/public/checkin/${id}`, { token, status_location: status });
      toast.success(`${info.full_name}: ${STATUS_META[status].label}`);
      setInfo((prev) => ({ ...prev, status_location: status, status_updated_at: new Date().toISOString() }));
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudo actualizar");
    } finally {
      setSaving(null);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-black text-white flex flex-col items-center justify-center px-6">
        <Loader2 className="w-10 h-10 animate-spin text-yellow-400 mb-4" />
        <div className="text-white/60 text-sm mb-6">Cargando información del campista…</div>
        {isKioskoUser && (
          <Button
            onClick={() => { window.location.assign("/kiosko/scan"); }}
            data-testid="loading-back-scan"
            className="bg-yellow-400/10 border border-yellow-400/40 text-yellow-300 hover:bg-yellow-400 hover:text-black"
          >
            <ArrowLeft className="w-4 h-4 mr-1" /> Volver al escáner
          </Button>
        )}
      </div>
    );
  }
  if (error || !info) {
    return (
      <div className="min-h-screen bg-black text-white flex items-center justify-center px-6 text-center">
        <div>
          <div className="text-red-400 text-lg font-bold mb-2">Error</div>
          <div className="text-white/70 text-sm">{error || "Registro no encontrado"}</div>
          <div className="mt-6 flex flex-col gap-2 items-stretch">
            <Button onClick={() => load()} className="bg-yellow-400 hover:bg-yellow-500 text-black font-bold uppercase tracking-widest" data-testid="checkin-retry">
              Reintentar
            </Button>
            {isKioskoUser && (
              <Button
                onClick={() => window.location.assign("/kiosko/scan")}
                variant="outline"
                data-testid="error-back-scan"
                className="border-white/20 text-white hover:bg-white/10"
              >
                <ArrowLeft className="w-4 h-4 mr-1" /> Volver al escáner
              </Button>
            )}
            <Button onClick={() => nav("/")} variant="ghost" className="text-white/50">Ir al inicio</Button>
          </div>
        </div>
      </div>
    );
  }

  const currentMeta = STATUS_META[info.status_location] || STATUS_META.not_checked_in;
  const CurrentIcon = currentMeta.icon;
  const currentIdx = STATUS_INDEX[info.status_location] ?? 0;
  const updated = info.status_updated_at ? new Date(info.status_updated_at) : null;

  return (
    <div className="min-h-screen bg-neutral-950 text-white flex flex-col overflow-x-hidden" data-testid="public-checkin-page">
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top,_rgba(250,204,21,0.08),_transparent_60%)] pointer-events-none" />

      <div className="relative z-10 max-w-md mx-auto w-full flex-1 flex flex-col p-6">
        {/* Sticky top back-to-scanner button for kiosko flow */}
        {isKioskoUser && (
          <button
            onClick={() => window.location.assign("/kiosko/scan")}
            data-testid="back-to-scanner-top"
            className="mb-4 self-start inline-flex items-center gap-1 px-3 py-2 rounded-lg bg-yellow-400/10 border border-yellow-400/40 text-yellow-300 hover:bg-yellow-400 hover:text-black text-xs uppercase tracking-widest font-bold"
          >
            <ArrowLeft className="w-4 h-4" /> Escanear otro
          </button>
        )}

        <div className="text-center mb-6">
          <div className="text-[10px] uppercase tracking-[0.4em] text-yellow-400/70 mb-1">Check-in</div>
          <div className="font-heading uppercase text-xl text-yellow-400 tracking-widest">Operación Rescate</div>
        </div>

        {/* Camper card */}
        <div className="bg-white/5 border-2 border-yellow-400/40 rounded-2xl p-5 mb-4 backdrop-blur">
          <div className="flex items-center gap-4">
            {info.photo ? (
              <img src={info.photo} alt="" className="w-20 h-20 rounded-full object-cover border-2 border-yellow-400" />
            ) : (
              <div className="w-20 h-20 rounded-full flex items-center justify-center border-2 border-yellow-400/40" style={{ background: info.color || "#525252" }}>
                <UserIcon className="w-10 h-10 text-white/80" />
              </div>
            )}
            <div className="flex-1 min-w-0">
              <div className="text-xs text-yellow-400 font-mono font-bold">{info.camper_number_display}</div>
              <div className="font-bold text-white text-lg leading-tight break-words">{info.full_name}</div>
              {info.color && <div className="mt-1 inline-flex items-center gap-1 text-[10px] uppercase tracking-widest text-white/50"><span className="w-2 h-2 rounded-full" style={{ background: info.color }} /> color asignado</div>}
            </div>
          </div>
        </div>

        {/* Payment card — big green PAGADO or red NO PAGADO */}
        <div
          data-testid="payment-info"
          className={`rounded-2xl p-5 mb-4 border-2 text-center ${
            info.authorized
              ? "bg-green-500/20 border-green-500"
              : "bg-red-500/20 border-red-500 animate-pulse"
          }`}
        >
          {info.authorized ? (
            <>
              <CheckCircle2 className="w-14 h-14 mx-auto text-green-300 mb-2" />
              <div className="font-heading uppercase text-4xl text-green-300 tracking-widest">PAGADO</div>
              {info.is_credit && (
                <div className="mt-2 inline-flex items-center gap-1 px-2 py-1 rounded-full bg-orange-500/30 border border-orange-400 text-orange-200 text-[10px] font-bold uppercase tracking-widest">
                  <DollarSign className="w-3 h-3" /> a crédito · pendiente ${info.remaining_amount}
                </div>
              )}
            </>
          ) : (
            <>
              <XCircle className="w-14 h-14 mx-auto text-red-300 mb-2" />
              <div className="font-heading uppercase text-4xl text-red-300 tracking-widest">NO PAGADO</div>
              <div className="mt-2 text-white/90 text-sm tabular-nums">
                Debe: <span className="font-bold text-red-200">${info.remaining_amount}</span> de ${info.total_amount}
              </div>
            </>
          )}
          {info.debt_note && (
            <div className="mt-3 text-white/70 text-[11px] italic border-t border-white/10 pt-2">📝 {info.debt_note}</div>
          )}
        </div>

        {/* Current status */}
        <div className={`rounded-2xl p-4 mb-5 text-center text-white ${currentMeta.color}`} data-testid="current-status">
          <CurrentIcon className="w-8 h-8 mx-auto mb-1" />
          <div className="text-[10px] uppercase tracking-[0.3em] opacity-70">Estado actual</div>
          <div className="font-heading uppercase text-xl tracking-wide">{currentMeta.label}</div>
          {updated && (
            <div className="text-[10px] opacity-75 mt-1">
              actualizado {updated.toLocaleString("es", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" })}
            </div>
          )}
        </div>

        {/* Status buttons — forward only */}
        <div className="space-y-2">
          <div className="text-[10px] uppercase tracking-[0.3em] text-white/50 text-center mb-2">Cambiar estado (solo avanzar)</div>
          {STATUS_ORDER.map((s) => {
            const meta = STATUS_META[s];
            const Icon = meta.icon;
            const idx = STATUS_INDEX[s];
            const active = info.status_location === s;
            const isBackward = idx < currentIdx;
            const disabled = active || isBackward || saving !== null;
            return (
              <button
                key={s}
                onClick={() => !disabled && update(s)}
                disabled={disabled}
                data-testid={`checkin-set-${s}`}
                className={`w-full flex items-center gap-3 px-4 py-4 rounded-2xl border-2 transition-all font-bold uppercase tracking-wider text-sm ${
                  active
                    ? "bg-white/5 border-white/10 text-white/40 cursor-default"
                    : isBackward
                      ? "bg-white/5 border-white/5 text-white/20 cursor-not-allowed"
                      : "bg-white/5 border-yellow-400/30 hover:bg-yellow-400 hover:text-black hover:border-yellow-400 active:scale-95"
                }`}
              >
                {saving === s
                  ? <Loader2 className="w-6 h-6 animate-spin" />
                  : active
                    ? <CheckCircle2 className="w-6 h-6 text-green-400" />
                    : isBackward
                      ? <Lock className="w-6 h-6" />
                      : <Icon className="w-6 h-6" />
                }
                <span className="flex-1 text-left">{meta.label}</span>
                {isBackward && <XCircle className="w-4 h-4 text-white/20" />}
              </button>
            );
          })}
        </div>

        <div className="mt-6 text-center text-[10px] uppercase tracking-widest text-white/30">
          Solo líderes autorizados · Los estados solo avanzan · Errores → pedir a admin
        </div>

        {/* Big scan-next CTA (kiosko flow) */}
        {isKioskoUser && (
          <button
            onClick={() => window.location.assign("/kiosko/scan")}
            data-testid="scan-next-cta"
            className="mt-6 w-full flex items-center justify-center gap-3 rounded-2xl border-2 border-yellow-400 bg-yellow-400 text-black py-5 font-heading uppercase tracking-widest text-xl hover:bg-yellow-300 active:scale-95 transition"
          >
            <ScanLine className="w-6 h-6" />
            Escanear otra persona
          </button>
        )}
      </div>
    </div>
  );
}
